import { EU_COUNTRIES } from '@naturalcycles/shared'
import { brandPurple } from '@src/cnst/colors.cnst'
import { useAppSelector } from '@src/hooks/useAppSelector'
import {
  GeolocationCountry,
  selectGeolocationCountry,
} from '@src/store/geolocation/geolocation.slice'
import classNames from 'classnames'
import { DatoCmsFileField } from 'dato-cms-assets'
import { ReactNode } from 'react'
import { Button } from '../button/Button.component'
import { ClientOnly } from '../client-only/ClientOnly.component'
import { NumberImage } from '../number-image/NumberImage.component'
import styles from './HardwareHowTo.module.scss'

export interface HardwareHowToProps {
  heading: string
  headingPlacement?: 'left' | 'center'
  headingSize?: 'title-xl' | 'title-l'
  numberHeadingSize?: 'default' | 'body-m'
  extraHeadingPadding?: boolean
  subheading?: string
  usageSteps: {
    heading: string
    image: DatoCmsFileField
  }[]
  ctaButton?: {
    text: string
    url: string
  }[]
  theme: 'light' | 'dark'
}

export function HardwareHowTo({
  heading,
  subheading,
  usageSteps,
  ctaButton,
  headingSize = 'title-xl',
  numberHeadingSize = 'default',
  theme = 'dark',
  extraHeadingPadding = true,
  headingPlacement = 'left',
}: HardwareHowToProps): ReactNode {
  const country = useAppSelector(selectGeolocationCountry)

  const sectionClass = classNames(styles.HardwareHowTo, {
    [styles.HardwareHowTo___light]: theme === 'light',
  })

  const headingClass = classNames(styles.HardwareHowTo__heading, {
    [styles.HardwareHowTo__heading___small]: headingSize === 'title-l',
    [styles.HardwareHowTo__heading___extraPadding]: !subheading && extraHeadingPadding,
    [styles.HardwareHowTo__heading___centered]: headingPlacement === 'center',
  })

  return (
    <section className={sectionClass}>
      <div className={styles.HardwareHowTo__container}>
        <h2 className={headingClass}>{heading}</h2>
        {subheading && <p className={styles.HardwareHowTo__subheading}>{subheading}</p>}
        <div className={styles.HardwareHowTo__grid}>
          {usageSteps.map((step, i) => (
            <div key={step.heading} className={styles.HardwareHowTo__gridItem}>
              <div
                className={`${styles.HardwareHowTo__gridHeading} ${
                  numberHeadingSize === 'body-m' ? styles.HardwareHowTo__gridHeading___small : ''
                }`}
              >
                <NumberImage ariaLabel={`step ${i + 1}`} color={brandPurple} numeral={i + 1} />
                <p>{step.heading}</p>
              </div>

              <img
                className={styles.HardwareHowTo__image}
                src={step.image.url}
                alt={step.image.alt}
              />
            </div>
          ))}
        </div>
      </div>
      <ClientOnly>
        {ctaButton && (EU_COUNTRIES.includes(country) || country === GeolocationCountry.US) && (
          <div className={styles.HardwareHowTo__button}>
            <Button theme="ghost" href={ctaButton[0]!.url}>
              {ctaButton[0]!.text}
            </Button>
          </div>
        )}
      </ClientOnly>
    </section>
  )
}
